import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft,
  Building,
  CreditCard,
  Save, 
  Phone,
  FileText,
  ChevronDown,
  ChevronUp,
  Trash2,
  Sparkles,
  X,
  Scan,
  QrCode,
  CheckCircle,
  MapPin,
  Map,
  Plus,
  Navigation,
  Camera
} from 'lucide-react';
// Import scanner libraries
import QrScanner from 'qr-scanner';
import { 
  createDocument, 
  getDocuments, 
  findSimilarAreas,
  findSimilarLocalities,
  createAreaFromGeocode,
  createLocalityFromGeocode,
  updateLocalityPincode,
  createOutletWithSerial,
  quickSaveOutlet,
  updateDocument
} from '../services/firebase';
import { geocodingService } from '../services/geocoding';
import { useAuth } from '../contexts/AuthContext';
import { City, Area, Locality, OutletClassification } from '../types';
import { addressCache } from '../utils/addressCache';

// Import modular components
import { 
  useDataLoader as useDataLoaderHook,
  useCameraManager as useCameraManagerHook, 
  useLocationManager as useLocationManagerHook,
  useMapManager as useMapManagerHook 
} from './add-outlet/hooks';
import {
  HeaderSection,
  NameSection, 
  AddressSection,
  LocationSection,
  MobileQRSection,
  RatingDetailsSection,
  PhotosDocumentsSection
} from './add-outlet/sections';
import { 
  CameraModal,
  MapAdjustmentModal,
  LocationConfirmationModal,
  QRScannerModal,
  LocationPermissionModal
} from './add-outlet/modals';

interface AddOutletProps {
  onBack: () => void;
  editMode?: boolean;
  existingOutlet?: any;
  // New props for external save handling
  onSave?: (outletData: any) => Promise<void>;
  saving?: boolean;
  userRole?: 'salesman' | 'warehouse' | 'admin';
}

// Initialize custom hooks from modular files


const AddOutlet: React.FC<AddOutletProps> = ({ 
  onBack, 
  editMode = false, 
  existingOutlet,
  onSave: externalOnSave,
  saving: externalSaving = false,
  userRole = 'salesman'
}) => {
  const { currentUser } = useAuth();
  
  // Debug role information
  console.log('🔍 AddOutlet Role Debug:', {
    userRoleProp: userRole,
    currentUserRole: currentUser?.role,
    currentUserId: currentUser?.id,
    isWarehouseUser: userRole === 'warehouse' || currentUser?.role === 'warehouse',
    isAdminUser: userRole === 'admin' || currentUser?.role === 'admin'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    building: '',
    streetName: '',
    landmark: '',
    subLocality: '',
    areaId: '',
    areaName: '',
    localityId: '',
    localityName: '',
    mobile: '',
    mobile2: '',
    mobile3: '',
    classificationId: '',
    classificationName: '',
    areaInSqft: '',
    rating: 0,
    latitude: 0,
    longitude: 0,
    outletInteriorExteriorPhotos: [] as string[],
    businessCardPhoto: '' as string, // Dedicated field for visiting card
    wtfBrandingStickerPhoto: '' as string, // Dedicated field for WTF contact sticker
    qrCodeData: '' as string, // Dedicated field for QR code content
    remarks: '',
    hasExoticFruits: false, // Exotic fruits availability
    hasHomeDelivery: false  // Home delivery availability
  });

  // State to track which fields are focused to manage placeholder visibility
  const [focusedFields, setFocusedFields] = useState<{ [key: string]: boolean }>({});

  // UI state for expanded sections
  const [showAdditionalMobiles, setShowAdditionalMobiles] = useState(false);

  // Firebase data states
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [localities, setLocalities] = useState<Locality[]>([]);
  const [classifications, setClassifications] = useState<OutletClassification[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  const [saving, setSaving] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Unified camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState<'outlet' | 'visiting-card' | 'wtf-sticker'>('outlet');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [tempLocation, setTempLocation] = useState({ lat: 0, lng: 0 });
  const [currentLocation, setCurrentLocation] = useState({ lat: 0, lng: 0 });
  
  // Map refs
  const locationMapInstanceRef = useRef<any>(null);
  
  // Auto-fill states
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [showAddressConfirmationModal, setShowAddressConfirmationModal] = useState(false);
  const [addressConfirmationData, setAddressConfirmationData] = useState<{
    addressFields: any;
    warehouseId: string;
    existingArea: any | null;
    existingLocalities: any[];
    editableAreaName: string;
    editableLocalityName: string;
  } | null>(null);
  const [currentWarehouseId, setCurrentWarehouseId] = useState<string>('');
  
  // Location confirmation states
  const [showLocationConfirmationModal, setShowLocationConfirmationModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState({ lat: 0, lng: 0 });
  const [confirmationMapLoaded, setConfirmationMapLoaded] = useState(false);
  const [locationFlowActive, setLocationFlowActive] = useState(false);
  const [extractedAddressData, setExtractedAddressData] = useState<{
    addressFields: any;
    warehouseId: string;
    existingArea: any | null;
    existingLocalities: any[];
    editableAreaName: string;
    editableLocalityName: string;
  } | null>(null);
  
  // Separate state to ensure pincode is never lost
  const [extractedPincode, setExtractedPincode] = useState<string>('');
  
  // Track last extracted coordinates to avoid unnecessary re-extractions
  const [lastExtractedCoordinates, setLastExtractedCoordinates] = useState({ lat: 0, lng: 0 });
  
  // QR scanner states
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScannerStream, setQRScannerStream] = useState<MediaStream | null>(null);
  const [isProcessingQR, setIsProcessingQR] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const confirmationMapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const confirmationMapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const currentLocationMarkerRef = useRef<any>(null);

  // Load data from Firebase on mount and handle edit mode initialization
  useEffect(() => {
    const initializeComponent = async () => {
      await loadInitialData();
      
      // Pre-populate form fields if in edit mode - after data is loaded
      if (editMode && existingOutlet) {
        await initializeEditMode();
      } else {
        // Auto-start location gathering only for add mode
        setTimeout(() => {
          autoGetCurrentLocation();
        }, 1000); // Small delay to ensure component is fully mounted
      }
    };
    
    initializeComponent();
  }, [editMode, existingOutlet]);

  // Initialize edit mode with proper hierarchical data population
  const initializeEditMode = async () => {
    if (!existingOutlet) return;
    
    try {
      console.log('Initializing edit mode for outlet:', existingOutlet.name);
      console.log('Existing outlet IDs:', {
        localityId: existingOutlet.localityId,
        areaId: existingOutlet.areaId
      });
      
      // Get all areas and localities data
      const [areasData, localitiesData] = await Promise.all([
        getDocuments('areas'),
        getDocuments('localities')
      ]);
      
      // Find the outlet's area and locality by their IDs
      let outletArea: any = null;
      let outletLocality: any = null;
      
      if (existingOutlet.localityId) {
        outletLocality = localitiesData.find((l: any) => l.id === existingOutlet.localityId);
        if (outletLocality) {
          // Get area from locality's areaId
          outletArea = areasData.find((a: any) => a.id === outletLocality.areaId);
        }
      } else if (existingOutlet.areaId) {
        // Fallback to direct area lookup
        outletArea = areasData.find((a: any) => a.id === existingOutlet.areaId);
      }
      
      console.log('Found outlet area:', outletArea);
      console.log('Found outlet locality:', outletLocality);
      
      // Prepare areas dropdown - include active areas + outlet's area if it's inactive
      let availableAreas = areasData.filter((a: any) => a.isActive);
      if (outletArea && !availableAreas.find((a: any) => a.id === outletArea.id)) {
        console.log('Adding inactive outlet area to dropdown:', outletArea.name);
        availableAreas.push(outletArea);
      }
      
      // Prepare localities dropdown - if we have an area, load its localities
      let availableLocalities: any[] = [];
      if (outletArea) {
        availableLocalities = localitiesData.filter((l: any) => l.areaId === outletArea.id && l.isActive);
        // Include outlet's locality if it's inactive
        if (outletLocality && !availableLocalities.find((l: any) => l.id === outletLocality.id)) {
          console.log('Adding inactive outlet locality to dropdown:', outletLocality.name);
          availableLocalities.push(outletLocality);
        }
      }
      
      // Set the dropdown options
      setAreas(availableAreas);
      setLocalities(availableLocalities);
      
      // Set form data with found IDs and names
      setFormData({
        name: existingOutlet.name === 'Unnamed Outlet' ? '' : (existingOutlet.name || ''),
        building: existingOutlet.building || '',
        streetName: existingOutlet.streetName || '',
        landmark: existingOutlet.landmark || '',
        subLocality: existingOutlet.subLocality || '',
        areaId: outletArea?.id || '',
        areaName: outletArea?.name || '',
        localityId: outletLocality?.id || '',
        localityName: outletLocality?.name || '',
        mobile: existingOutlet.mobile || '',
        mobile2: existingOutlet.mobile2 || '',
        mobile3: existingOutlet.mobile3 || '',
        classificationId: existingOutlet.classificationId || '',
        classificationName: existingOutlet.classificationName || '',
        areaInSqft: existingOutlet.areaInSqft ? existingOutlet.areaInSqft.toString() : '',
        rating: existingOutlet.rating || 0,
        latitude: existingOutlet.latitude || 0,
        longitude: existingOutlet.longitude || 0,
        // Handle both old and new field names during transition
        outletInteriorExteriorPhotos: existingOutlet.outletInteriorExteriorPhotos || existingOutlet.photos || [],
        businessCardPhoto: existingOutlet.businessCardPhoto || existingOutlet.visitingCardImage || '',
        wtfBrandingStickerPhoto: existingOutlet.wtfBrandingStickerPhoto || existingOutlet.wtfStickerImage || '',
        qrCodeData: existingOutlet.qrCodeData || '',
        remarks: existingOutlet.remarks || '',
        hasExoticFruits: existingOutlet.hasExoticFruits || false,
        hasHomeDelivery: existingOutlet.hasHomeDelivery || false
      });
      
      console.log('Form data set with area/locality:', {
        areaId: outletArea?.id,
        areaName: outletArea?.name,
        localityId: outletLocality?.id,
        localityName: outletLocality?.name
      });
      
      // Store pincode for edit mode
      if (existingOutlet.pincode) {
        setExtractedAddressData({
          addressFields: { pincode: existingOutlet.pincode },
          warehouseId: '',
          existingArea: null,
          existingLocalities: [],
          editableAreaName: '',
          editableLocalityName: ''
        });
      }
      
    } catch (error) {
      console.error('Error initializing edit mode:', error);
    }
  };

  // Initialize map in location confirmation modal
  useEffect(() => {
    if (showLocationConfirmationModal && pendingLocation.lat !== 0 && pendingLocation.lng !== 0) {
      setConfirmationMapLoaded(false);
      setTimeout(() => {
        initializeConfirmationMap(pendingLocation.lat, pendingLocation.lng);
      }, 100);
    }
  }, [showLocationConfirmationModal, pendingLocation]);

  // Camera stream handled by unified CameraModal component


  const loadInitialData = async () => {
    try {
      setDataLoading(true);
      const [areasData, warehousesData, routesData, beatsData, classificationsData] = await Promise.all([
        getDocuments('areas'),
        getDocuments('warehouses'),
        getDocuments('routes'),
        getDocuments('beats'),
        getDocuments('outlet_classifications')
      ]);
      
      setClassifications(classificationsData.filter((cls: OutletClassification) => cls.isActive));
      
      // Get current user's route from beats table (not users table)
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '-'); // Format: YYYY-MM-DD
      const userActiveBeat = beatsData.find((beat: any) => 
        beat.salesmanId === currentUser?.id && 
        beat.isActive && 
        beat.forDate === today
      );
      
      if (userActiveBeat && userActiveBeat.routeId) {
        // Find the user's route from the beat assignment by custom routeId
        const userRoute = routesData.find((route: any) => route.routeId === userActiveBeat.routeId);
        if (userRoute && userRoute.warehouseId) {
          // Filter areas to only show those belonging to the same warehouse as the user's route
          const filteredAreas = areasData.filter((area: Area) => 
            area.warehouseId === userRoute.warehouseId && area.isActive
          );
          setAreas(filteredAreas);
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const loadAreasForCity = async (cityId: string) => {
    try {
      const [areasData, warehousesData, usersData] = await Promise.all([
        getDocuments('areas'),
        getDocuments('warehouses'),
        getDocuments('users')
      ]);
      
      // Get current user's warehouse information directly from user record
      const currentUserData = usersData.find((user: any) => user.id === currentUser?.id);
      const userWarehouseId = currentUserData?.warehouseId;
      
      if (userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin') {
        // No restriction - show all active areas for warehouse/admin users
        const filteredAreas = areasData.filter((area: Area) => area.isActive);
        setAreas(filteredAreas);
        return;
      }
      
      if (userWarehouseId) {
        // Salesman restriction - show only areas from their warehouse
        const filteredAreas = areasData.filter((area: Area) => 
          area.warehouseId === userWarehouseId && area.isActive
        );
        setAreas(filteredAreas);
        return;
      }
      
      // Fallback: Filter areas based on warehouses that belong to the selected city
      const cityWarehouses = warehousesData.filter((warehouse: any) => warehouse.cityCode === cityId);
      const warehouseIds = cityWarehouses.map((warehouse: any) => warehouse.id);
      const filteredAreas = areasData.filter((area: Area) => 
        warehouseIds.includes(area.warehouseId) && area.isActive
      );
      
      setAreas(filteredAreas);
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  };

  const loadLocalitiesForArea = async (areaId: string) => {
    try {
      const localitiesData = await getDocuments('localities');
      const filteredLocalities = localitiesData.filter((locality: Locality) => locality.areaId === areaId && locality.isActive);
      setLocalities(filteredLocalities);
    } catch (error) {
      console.error('Error loading localities:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      
      // Handle area selection
      if (name === 'areaId') {
        const selectedArea = areas.find(area => area.id === value);
        if (selectedArea) {
          newData.areaName = selectedArea.name;
          loadLocalitiesForArea(value);
          // Reset locality when area changes
          newData.localityId = '';
          newData.localityName = '';
        }
      }
      
      // Handle locality selection
      if (name === 'localityId') {
        const selectedLocality = localities.find(locality => locality.id === value);
        if (selectedLocality) {
          newData.localityName = selectedLocality.name;
        }
      }
      
      // Handle classification selection
      if (name === 'classificationId') {
        const selectedClassification = classifications.find(cls => cls.id === value);
        if (selectedClassification) {
          newData.classificationName = selectedClassification.name;
        }
      }
      
      return newData;
    });
  };

  // Handle focus events to hide placeholder text immediately
  const handleFocus = (fieldName: string) => {
    setFocusedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Handle blur events to show placeholder text if field is empty
  const handleBlur = (fieldName: string) => {
    setFocusedFields(prev => ({ ...prev, [fieldName]: false }));
  };

  // Get effective placeholder - empty string if focused and field is empty
  const getPlaceholder = (fieldName: string, defaultPlaceholder: string) => {
    const isFocused = focusedFields[fieldName];
    const fieldValue = (formData as any)[fieldName];
    
    // If focused and field is empty, don't show placeholder
    if (isFocused && !fieldValue) {
      return '';
    }
    
    return defaultPlaceholder;
  };

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lng2-lng1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  };

  // Smart address extraction with distance-based re-extraction
  const extractAddressIfNeeded = async (latitude: number, longitude: number, force: boolean = false) => {
    const disableDistanceReuse = process.env.REACT_APP_DISABLE_DISTANCE_REUSE === 'true';
    if (disableDistanceReuse) {
      console.log('🛑 Distance-based reuse disabled via REACT_APP_DISABLE_DISTANCE_REUSE');
    }
    // If we have previous extraction coordinates, check if we need to re-extract
    if (!disableDistanceReuse && !force && lastExtractedCoordinates.lat !== 0 && lastExtractedCoordinates.lng !== 0) {
      const distance = calculateDistance(
        lastExtractedCoordinates.lat, 
        lastExtractedCoordinates.lng, 
        latitude, 
        longitude
      );
      
      console.log(`📍 Distance from last extraction: ${distance.toFixed(0)}m`);
      
      // If distance is less than 150m, reuse existing address data
      if (distance < 150) {
        console.log('🔄 Reusing existing address data (distance < 150m)');
        return;
      } else {
        console.log('🆕 Re-extracting address data (distance >= 150m)');
      }
    }

    // Extract address data
    await extractAddressFromLocation(latitude, longitude);
    
    // Update last extracted coordinates
    setLastExtractedCoordinates({ lat: latitude, lng: longitude });
  };

  // Auto-capture location on component mount
  const autoGetCurrentLocation = () => {
    setLocationLoading(true);
    setLocationFlowActive(true);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setLocationLoading(false);
      setLocationFlowActive(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Store in pending location first (not formData)
        setPendingLocation({ lat, lng });
        setCurrentLocation({ lat, lng });
        
        // Wait for Google Maps to be fully loaded before extracting address
        
        // First, trigger Google Maps loading if not already done
        try {
          await loadGoogleMaps();
          console.log('✅ Google Maps ready for address extraction');
        } catch (error) {
          console.error('❌ Failed to load Google Maps API:', error);
        }
        
        // Additional wait to ensure all components are ready
        await new Promise(resolve => setTimeout(resolve, 500)); // Reduced from 1000ms
        
        // Extract address immediately after GPS capture (force on first capture)
        await extractAddressIfNeeded(lat, lng, true);
        
        setLocationLoading(false);
        
        // Show location confirmation modal with extracted address data
        setShowLocationConfirmationModal(true);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationLoading(false);
        setLocationFlowActive(false);
        // Silently fail for auto-capture - user can manually trigger later
      },
      options
    );
  };

  const getCurrentLocation = () => {
    setLocationLoading(true);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setShowLocationModal(true);
      setLocationLoading(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Store in pending location first (not formData)
        setPendingLocation({ lat, lng });
        setCurrentLocation({ lat, lng });
        
        // Extract address immediately after GPS capture
        await extractAddressFromLocation(lat, lng);
        
        setLocationLoading(false);
        
        // Show location confirmation modal with extracted address data
        setShowLocationConfirmationModal(true);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationError('Unable to get location. Please ensure location services are enabled.');
        setShowLocationModal(true);
        setLocationLoading(false);
      },
      options
    );
  };

  // Extract address from location coordinates with smart caching
  const extractAddressFromLocation = async (latitude: number, longitude: number) => {
    try {
      // Cleanup expired cache entries first
      addressCache.cleanupExpiredEntries();
      // Env flags
      const disableAddressCache = process.env.REACT_APP_DISABLE_ADDRESS_CACHE === 'true';
      const disableSimilarityMatch = process.env.REACT_APP_DISABLE_SIMILARITY_MATCH === 'true';
      
      // Check cache first for nearby address data
      const cachedData = disableAddressCache ? null : addressCache.findNearbyCache(latitude, longitude);
      
      // Get current user's warehouse information directly from user record
      const usersData = await getDocuments('users');
      const warehousesData = await getDocuments('warehouses');
      const citiesData = await getDocuments('cities');
      
      const currentUserData = usersData.find((user: any) => user.id === currentUser?.id);
      const warehouseId = currentUserData?.warehouseId || '';

      console.log('👤 User warehouse lookup:', {
        currentUserId: currentUser?.id,
        foundUser: !!currentUserData,
        warehouseId,
        userRoleProp: userRole,
        currentUserRole: currentUser?.role,
        isWarehouseOrAdmin: userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin'
      });

      // Allow warehouse/admin users (check both prop and actual user role)
      const isWarehouseOrAdmin = userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin';
      
      if (!warehouseId && !isWarehouseOrAdmin) {
        console.log('❌ No warehouse assignment found for address extraction.');
        return;
      }

      if (warehouseId) {
        setCurrentWarehouseId(warehouseId);
      }

      // If we have cached data, validate and use it immediately
      if (cachedData) {
        console.log('🎯 Found cached address data, validating...');
        
        // Get current areas and localities for validation
        const [areasData, localitiesData] = await Promise.all([
          getDocuments('areas'),
          getDocuments('localities')
        ]);
        
        // Validate cached data against current database
        const isValid = await addressCache.validateCachedData(cachedData, areasData, localitiesData);
        
        if (isValid && cachedData.warehouseId === warehouseId) {
          console.log('✅ Using valid cached address data');
          
          // Set extracted address data immediately from cache
          setExtractedAddressData({
            addressFields: cachedData.addressFields,
            warehouseId: cachedData.warehouseId,
            existingArea: cachedData.existingArea,
            existingLocalities: cachedData.existingLocalities,
            editableAreaName: cachedData.areaName,
            editableLocalityName: cachedData.localityName
          });

          // Store pincode from cache
          if (cachedData.addressFields.pincode) {
            setExtractedPincode(cachedData.addressFields.pincode);
            console.log('🏷️ Pincode loaded from cache:', cachedData.addressFields.pincode);
          }
          
          // Start background refresh for accuracy (non-blocking)
          if (!disableAddressCache) {
            refreshAddressDataInBackground(latitude, longitude, warehouseId, citiesData);
          }
          return;
        } else {
          console.log('❌ Cached data invalid or warehouse mismatch, proceeding with fresh extraction');
        }
      }

      // Fresh extraction (either no cache or invalid cache)
      console.log('🆕 Performing fresh address extraction');
      await performFreshAddressExtraction(latitude, longitude, warehouseId, citiesData);

    } catch (error) {
      console.error('Error extracting address:', error);
    }
  };

  // Perform fresh address extraction and cache the results
  const performFreshAddressExtraction = async (
    latitude: number, 
    longitude: number, 
    warehouseId: string, 
    citiesData: any[]
  ) => {
    try {
      const disableAddressCache = process.env.REACT_APP_DISABLE_ADDRESS_CACHE === 'true';
      const disableSimilarityMatch = process.env.REACT_APP_DISABLE_SIMILARITY_MATCH === 'true';
      // Get the allowed city from warehouse (only for salesmen)
      const warehousesData = await getDocuments('warehouses');
      const warehouse = warehousesData.find((w: any) => w.warehouseId === warehouseId);
      
      console.log('🏢 Warehouse lookup:', { 
        warehouseId, 
        foundWarehouse: !!warehouse,
        warehouseName: warehouse?.name,
        cityCode: warehouse?.cityCode,
        userRole 
      });
      
      const allowedCity = (userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin') ? 
        undefined : // No city restriction for warehouse/admin
        warehouse?.cityCode ? 
          citiesData.find((c: any) => c.cityCode === warehouse.cityCode)?.name : undefined;
      
      // Parse address from geocoding with city validation
      const parseResult = await geocodingService.parseForOutletFields(
        latitude, 
        longitude, 
        allowedCity
      );
      
      if (!parseResult.success || !parseResult.addressFields) {
        console.log('Failed to extract address from location.');
        if (parseResult && (parseResult as any).error) {
          alert(`Failed to extract address from location.\n\nDetails: ${(parseResult as any).error}`);
        } else {
          alert('Failed to extract address from location.');
        }
        return;
      }

      const addressFields = parseResult.addressFields;

      // Check for similar areas and localities
      let existingArea = null;
      let existingLocalities: any[] = [];
      
      if (!disableSimilarityMatch && addressFields.suggestedAreaName) {
        let similarAreas = [];
        
        // For warehouse/admin users, search across ALL areas if no warehouseId
        const isWarehouseOrAdmin = userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin';
        
        console.log('🔍 Area search debug:', {
          suggestedAreaName: addressFields.suggestedAreaName,
          warehouseId,
          isWarehouseOrAdmin,
          willSearchAllAreas: isWarehouseOrAdmin && !warehouseId,
          willSearchWarehouseAreas: !!warehouseId
        });
        
        if (isWarehouseOrAdmin && !warehouseId) {
          console.log('🔍 Warehouse user: searching ALL areas for:', addressFields.suggestedAreaName);
          // Get all areas and search manually since we don't have warehouse restriction
          const allAreas = await getDocuments('areas');
          const activeAreas = allAreas.filter((area: any) => area.isActive);
          
          console.log('📊 Total active areas found:', activeAreas.length);
          console.log('🗂️ All area names in database:', activeAreas.map((a: any) => a.name));
          
          // Manual similarity search across all areas  
          similarAreas = activeAreas.filter((area: any) => {
            const areaNameLower = area.name.toLowerCase().trim();
            const suggestedAreaLower = addressFields.suggestedAreaName.toLowerCase().trim();
            
            // Multiple matching strategies
            const exactMatch = areaNameLower === suggestedAreaLower;
            const containsMatch = areaNameLower.includes(suggestedAreaLower) || suggestedAreaLower.includes(areaNameLower);
            
            // Clean matching - remove parentheses and normalize spaces
            const cleanAreaName = areaNameLower
              .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents
              .replace(/\s+/g, ' ')           // Normalize multiple spaces
              .trim();
            const cleanSuggestedName = suggestedAreaLower
              .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents  
              .replace(/\s+/g, ' ')           // Normalize multiple spaces
              .trim();
            const cleanMatch = cleanAreaName === cleanSuggestedName;
            
            // Extra fuzzy matching for common patterns like "West" vs "(West)"
            const fuzzyAreaName = areaNameLower
              .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
              .replace(/\s+/g, ' ')                   // Normalize spaces
              .trim();
            const fuzzySuggestedName = suggestedAreaLower
              .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
              .replace(/\s+/g, ' ')                   // Normalize spaces
              .trim();
            const fuzzyMatch = fuzzyAreaName === fuzzySuggestedName;
            
            const isMatch = exactMatch || containsMatch || cleanMatch || fuzzyMatch;
            
            if (isMatch) {
              console.log('✅ Area match found:', {
                dbAreaName: area.name,
                suggestedName: addressFields.suggestedAreaName,
                cleanDbName: cleanAreaName,
                cleanSuggested: cleanSuggestedName,
                fuzzyDbName: fuzzyAreaName,
                fuzzySuggested: fuzzySuggestedName,
                matchType: exactMatch ? 'exact' : containsMatch ? 'contains' : cleanMatch ? 'clean' : 'fuzzy'
              });
            }
            
            return isMatch;
          });
          
          console.log('🎯 Found similar areas across all warehouses:', similarAreas.map((a: any) => a.name));
        } else if (warehouseId) {
          // Salesman or warehouse user with warehouseId: use warehouse-restricted search
          console.log('👤 Searching areas within warehouse:', warehouseId);
          similarAreas = await findSimilarAreas(addressFields.suggestedAreaName, warehouseId);
        }
        
        if (similarAreas.length > 0) {
          existingArea = similarAreas[0];
          console.log('✅ Using existing area:', existingArea.name);
          // If area exists, check for similar localities
          if (addressFields.suggestedLocalityName) {
            existingLocalities = await findSimilarLocalities(addressFields.suggestedLocalityName, existingArea.id);
            console.log('🏘️ Found similar localities:', existingLocalities.map((l: any) => l.name));
          }
        } else {
          console.log('➕ No similar areas found - will create new area');
        }
      } else if (disableSimilarityMatch) {
        console.log('🔕 Similarity matching disabled via REACT_APP_DISABLE_SIMILARITY_MATCH');
      }

      // Store extracted address data for location confirmation modal
      const extractedData = {
        addressFields,
        warehouseId: warehouseId || '', // Allow empty for warehouse/admin
        existingArea,
        existingLocalities,
        editableAreaName: addressFields.suggestedAreaName || '',
        editableLocalityName: addressFields.suggestedLocalityName || ''
      };
      
      setExtractedAddressData(extractedData);

      // Store pincode separately to ensure it's never lost
      if (addressFields.pincode) {
        setExtractedPincode(addressFields.pincode);
        console.log('🏷️ Pincode extracted and stored:', addressFields.pincode);
      }

      // Cache the successful extraction for future use
      if (!disableAddressCache && addressFields.suggestedAreaName && addressFields.suggestedLocalityName) {
        addressCache.storeAddressData(latitude, longitude, {
          areaName: addressFields.suggestedAreaName,
          areaId: existingArea?.id || '',
          localityName: addressFields.suggestedLocalityName,
          localityId: existingLocalities.length > 0 ? existingLocalities[0].id : '',
          existingArea,
          existingLocalities,
          addressFields,
          warehouseId
        });
      }

    } catch (error) {
      console.error('Error in fresh address extraction:', error);
    }
  };

  // Background refresh function (non-blocking)
  const refreshAddressDataInBackground = async (
    latitude: number, 
    longitude: number, 
    warehouseId: string, 
    citiesData: any[]
  ) => {
    try {
      console.log('🔄 Starting background address refresh...');
      
      // Small delay to avoid impacting UI responsiveness
      setTimeout(async () => {
        await performFreshAddressExtraction(latitude, longitude, warehouseId, citiesData);
        console.log('✅ Background refresh completed');
      }, 1000);
      
    } catch (error) {
      console.warn('Background address refresh failed:', error);
    }
  };

  const startCamera = async (type: 'outlet' | 'visiting-card' | 'wtf-sticker' = 'outlet') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setCameraStream(stream);
      setCameraType(type);
      setShowCamera(true);
    } catch (error) {
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const capturePhoto = () => {
    let videoElement: HTMLVideoElement | null = null;
    
    // Find the correct video element based on camera type
    switch (cameraType) {
      case 'outlet':
        videoElement = document.querySelector('video[data-attribute="camera-video"]') as HTMLVideoElement;
        break;
      case 'visiting-card':
        videoElement = document.querySelector('video[data-attribute="visiting-card"]') as HTMLVideoElement;
        break;
      case 'wtf-sticker':
        videoElement = document.querySelector('video[data-attribute="wtf-sticker"]') as HTMLVideoElement;
        break;
    }
    
    if (videoElement && canvasRef.current && videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (context) {
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        context.drawImage(videoElement, 0, 0);
        
        const photoDataURL = canvas.toDataURL('image/jpeg', 0.8);
        
        // Update form data based on camera type
        setFormData(prev => {
          switch (cameraType) {
            case 'outlet':
              return { ...prev, outletInteriorExteriorPhotos: [...prev.outletInteriorExteriorPhotos, photoDataURL] };
            case 'visiting-card':
              return { ...prev, businessCardPhoto: photoDataURL };
            case 'wtf-sticker':
              return { ...prev, wtfBrandingStickerPhoto: photoDataURL };
            default:
              return prev;
          }
        });
        
        stopCamera();
      }
    } else {
      alert('Camera not ready. Please wait a moment and try again.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Google Maps functions
  const loadGoogleMaps = () => {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if ((window as any).google && (window as any).google.maps) {
        resolve(true);
        return;
      }
      
      // Check if script is already loading
      if ((window as any).googleMapsLoading) {
        const checkInterval = setInterval(() => {
          if ((window as any).google && (window as any).google.maps) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
        return;
      }
      
      // Mark as loading
      (window as any).googleMapsLoading = true;
      
      // Create script element
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      console.log('🔑 Google Maps API Key check:', {
        keyExists: !!apiKey,
        keyLength: apiKey?.length,
        keyPrefix: apiKey?.substring(0, 8) + '...',
        envVarName: 'REACT_APP_GOOGLE_MAPS_API_KEY'
      });
      
      if (!apiKey) {
        console.error('❌ Google Maps API key not found in environment variables');
        reject(new Error('Google Maps API key not found'));
        return;
      }
      
  const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding&loading=async`;
      console.log('📡 Loading Google Maps script:', scriptUrl);
      
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        (window as any).googleMapsLoading = false;
        console.log('✅ Google Maps script loaded successfully');
        resolve(true);
      };
      
      script.onerror = (error) => {
        (window as any).googleMapsLoading = false;
        console.error('❌ Failed to load Google Maps script:', error);
        console.error('🔍 Script URL that failed:', script.src);
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  };

  const initializeConfirmationMap = async (lat?: number, lng?: number) => {
    if (!confirmationMapRef.current) return;
    
    // Use provided coordinates
    const centerLat = lat || 0;
    const centerLng = lng || 0;
    
    if (centerLat === 0 || centerLng === 0) {
      console.warn("Coordinates not set yet — not initializing confirmation map.");
      return;
    }
    
    try {
      // Ensure Google Maps API is fully loaded
      await loadGoogleMaps();
      
      const { google } = window as any;
      
      // Double check that all required Google Maps components are available
      if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
        console.log('🔄 Google Maps not fully loaded, retrying...');
        // Wait longer for all components to be available
        for (let i = 0; i < 3; i++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          if (google && google.maps && google.maps.Map && google.maps.Marker) {
            console.log('✅ Google Maps components loaded');
            break;
          }
        }
        
        if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
          console.error('❌ Google Maps API not fully loaded after retries');
          return;
        }
      }
      
      const mapOptions = {
        zoom: 16,
        center: { lat: centerLat, lng: centerLng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative'
      };

      const map = new google.maps.Map(confirmationMapRef.current, mapOptions);
      confirmationMapInstanceRef.current = map;

      // Wait for map to be fully initialized
      google.maps.event.addListenerOnce(map, 'idle', () => {
        // Add blue circle for detected location
        new google.maps.Marker({
          position: { lat: centerLat, lng: centerLng },
          map: map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#4285F4',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
          title: 'Detected Location',
          draggable: false
        });

        setConfirmationMapLoaded(true);
      });
    } catch (error) {
      console.error('Error initializing confirmation map:', error);
      // Handle critical errors like API key restrictions (same as adjustment map)
      if (error instanceof Error && error.message.includes('ReferrerNotAllowedMapError')) {
        alert(`Google Maps API key restrictions prevent loading. Current URL: ${window.location.href}\n\nPlease add this URL to your API key restrictions in Google Cloud Console.`);
      }
    }
  };

  const initializeMap = async (outletLat?: number, outletLng?: number, originalLat?: number, originalLng?: number) => {
    if (!mapRef.current) return;
    
    // Use provided coordinates or fall back to formData
    const centerLat = outletLat || formData.latitude;
    const centerLng = outletLng || formData.longitude;
    
    // Original GPS location (for blue marker)
    const originalLatitude = originalLat || currentLocation.lat;
    const originalLongitude = originalLng || currentLocation.lng;
    
    if (centerLat === 0 || centerLng === 0) {
      console.warn("Coordinates not set yet — not initializing map.");
      return;
    }
    
    try {
      // Ensure Google Maps API is fully loaded
      await loadGoogleMaps();
      
      const { google } = window as any;
      
      // Double check that all required Google Maps components are available
      if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
        console.log('Google Maps not fully loaded, retrying...');
        // Wait a bit more and try again
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
          console.error('Google Maps API not fully loaded after retry');
          return;
        }
      }
      
      const mapOptions = {
        zoom: 16,
        center: { lat: centerLat, lng: centerLng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative'
      };

      const map = new google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Wait for map to be fully initialized
      google.maps.event.addListenerOnce(map, 'idle', () => {
        // Add blue circle for original GPS location (user's current position) - only if we have original coordinates
        if (originalLatitude !== 0 && originalLongitude !== 0) {
          const currentLocationMarker = new google.maps.Marker({
            position: { lat: originalLatitude, lng: originalLongitude },
            map: map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: 'Your Current Location (GPS)',
            draggable: false
          });
          
          currentLocationMarkerRef.current = currentLocationMarker;
        }
        
        // Add red marker for outlet location (draggable)
        const outletMarker = new google.maps.Marker({
          position: { lat: centerLat, lng: centerLng },
          map: map,
          draggable: true,
          title: 'Outlet Location (Drag to adjust)',
          icon: {
            path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 6,
            fillColor: '#EA4335',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          }
        });
        
        markerRef.current = outletMarker;
        
        // Update temp location when marker is dragged (for confirm/cancel flow)
        outletMarker.addListener('dragend', () => {
          const position = outletMarker.getPosition();
          if (position) {
            setTempLocation({
              lat: position.lat(),
              lng: position.lng()
            });
          }
        });

        // Update temp location when map is clicked (for confirm/cancel flow)
        map.addListener('click', (event: any) => {
          const clickedLocation = event.latLng;
          outletMarker.setPosition(clickedLocation);
          setTempLocation({
            lat: clickedLocation.lat(),
            lng: clickedLocation.lng()
          });
        });

        setMapLoaded(true);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      // Only show error for critical issues, not for API loading problems
      if (error instanceof Error && error.message.includes('ReferrerNotAllowedMapError')) {
        alert(`Google Maps API key restrictions prevent loading. Current URL: ${window.location.href}\n\nPlease add this URL to your API key restrictions in Google Cloud Console.`);
      }
    }
  };



  // Auto-assign route to newly created locality
  const autoAssignRouteToNewLocality = async (localityId: string, localityData: any) => {
    try {
      console.log('🚀 Starting auto-route assignment for locality:', {
        localityId,
        localityName: localityData.name,
        areaId: localityData.areaId
      });

      // Find the area for this locality
      const area = areas.find(a => a.id === localityData.areaId);
      if (!area) {
        console.log('❌ No area found for auto-route assignment');
        return;
      }

      console.log('✅ Found area:', {
        areaId: area.id,
        areaName: area.name,
        areaWarehouseId: area.warehouseId
      });

      // Fetch warehouses, routes, and beats data
      const [warehousesData, routesData, beatsData] = await Promise.all([
        getDocuments('warehouses'),
        getDocuments('routes'),
        getDocuments('beats')
      ]);

      // Find the warehouse for this area (use dual lookup for compatibility)
      const warehouse = warehousesData.find((w: any) => w.warehouseId === area.warehouseId) || 
                       warehousesData.find((w: any) => w.id === area.warehouseId);
      if (!warehouse) {
        console.log('❌ No warehouse found for auto-route assignment');
        return;
      }

      console.log('✅ Found warehouse:', {
        warehouseId: warehouse.id,
        customWarehouseId: warehouse.warehouseId,
        warehouseName: warehouse.name
      });

      // First, try to get the user's currently assigned route
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '-');
      const userActiveBeat = beatsData.find((beat: any) => 
        beat.salesmanId === currentUser?.id && 
        beat.isActive && 
        beat.forDate === today
      );

      let targetRoute = null;

      if (userActiveBeat && userActiveBeat.routeId) {
        // Check if user's assigned route belongs to the same warehouse as the area
        const userRoute = routesData.find((route: any) => route.routeId === userActiveBeat.routeId);
        
        if (userRoute && userRoute.isActive && 
           (userRoute.warehouseId === area.warehouseId || userRoute.warehouseId === warehouse.warehouseId)) {
          targetRoute = userRoute;
          console.log('✅ Using user\'s assigned route:', {
            routeId: userRoute.routeId,
            routeName: userRoute.name,
            reason: 'User is assigned to this route and it matches the warehouse'
          });
        } else {
          console.log('⚠️ User\'s route doesn\'t match warehouse:', {
            userRouteId: userActiveBeat.routeId,
            userRouteWarehouse: userRoute?.warehouseId,
            areaWarehouse: area.warehouseId,
            warehouseId: warehouse.warehouseId
          });
        }
      }

      // If user's route doesn't work, fall back to any available route in the warehouse
      if (!targetRoute) {
        const eligibleRoutes = routesData.filter((route: any) => 
          (route.warehouseId === area.warehouseId || route.warehouseId === warehouse.warehouseId) && 
          route.isActive
        );

        console.log('🔍 Fallback route matching:', {
          areaWarehouseId: area.warehouseId,
          warehouseCustomId: warehouse.warehouseId,
          totalRoutes: routesData.length,
          eligibleRoutes: eligibleRoutes.length,
          allRoutes: routesData.map((r: any) => ({id: r.routeId, name: r.name, warehouseId: r.warehouseId, isActive: r.isActive}))
        });

        if (eligibleRoutes.length === 0) {
          console.log('❌ No active routes found for auto-assignment in warehouse:', warehouse.name);
          return;
        }

        // Use the first available route as fallback
        targetRoute = eligibleRoutes[0];
        console.log('⚠️ Using fallback route assignment:', {
          routeId: targetRoute.routeId,
          routeName: targetRoute.name,
          reason: 'User route unavailable, using first available route'
        });
      }

      console.log('🎯 Attempting to assign route:', {
        routeId: targetRoute.routeId,
        routeName: targetRoute.name,
        localityId
      });

      // Assign route to locality
      await updateDocument('localities', localityId, {
        routeId: targetRoute.routeId,
        routeName: targetRoute.name,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || ''
      });
      
      console.log(`✅ Auto-assigned locality "${localityData.name}" to route "${targetRoute.name}"`);
    } catch (error) {
      console.error('❌ Error in auto-route assignment:', error);
      // Don't show error to user since this is a nice-to-have feature
    }
  };

  // Edit location function - go directly to draggable map
  const handleEditLocation = () => {
    // Set up pending location with current form data
    setPendingLocation({
      lat: formData.latitude,
      lng: formData.longitude
    });
    setCurrentLocation({
      lat: formData.latitude,
      lng: formData.longitude
    });
    
    // Open MapAdjustmentModal directly - skip confirmation screen
    setShowMapModal(true);
  };

  // Confirm pending location with optional address creation
  const confirmPendingLocation = async () => {
    if (pendingLocation.lat !== 0 && pendingLocation.lng !== 0) {
      // Update formData with confirmed location
      setFormData(prev => ({
        ...prev,
        latitude: pendingLocation.lat,
        longitude: pendingLocation.lng
      }));
      
      // If user chose to create area/locality, handle database creation
      if (extractedAddressData && extractedAddressData.editableAreaName.trim()) {
        try {
          const { addressFields, warehouseId, existingArea, existingLocalities, editableAreaName, editableLocalityName } = extractedAddressData;
          
          let finalAreaId: string;
          let finalLocalityId: string;

          // Handle area creation/selection
          if (existingArea) {
            finalAreaId = existingArea.id;
          } else {
            // Create new area with editable name
            finalAreaId = await createAreaFromGeocode(
              editableAreaName || 'Unknown Area',
              warehouseId,
              currentUser?.id || ''
            );
          }

          // Handle locality creation/selection
          if (existingLocalities.length > 0) {
            finalLocalityId = existingLocalities[0].id;
            // Update pincode if missing
            if (addressFields.pincode) {
              await updateLocalityPincode(finalLocalityId, addressFields.pincode);
            }
          } else {
            // Create new locality with editable name
            finalLocalityId = await createLocalityFromGeocode(
              editableLocalityName || 'Unknown Locality',
              finalAreaId,
              addressFields.pincode,
              currentUser?.id || ''
            );
            
            // Auto-assign route to the newly created locality
            await autoAssignRouteToNewLocality(finalLocalityId, {
              name: editableLocalityName || 'Unknown Locality',
              areaId: finalAreaId
            });
          }

          // Populate form fields after database creation
          await populateFormFields(addressFields, finalAreaId, finalLocalityId);
          
        } catch (error) {
          console.error('Error creating area/locality:', error);
          alert('Error creating area/locality. Location confirmed but address details not saved.');
        }
      }
      
      setShowLocationConfirmationModal(false);
      setExtractedAddressData(null);
      setLocationFlowActive(false);
    }
  };

  // Allow user to adjust location on map - use same working logic as handleEditLocation
  const adjustLocationOnMap = () => {
    // Use same approach as handleEditLocation
    setShowLocationConfirmationModal(false);
    setShowMapModal(true);
  };

  const handleAutoFillFromLocation = async (lat?: number, lng?: number) => {
    // Use provided coordinates or fall back to formData
    const latitude = lat || formData.latitude;
    const longitude = lng || formData.longitude;
    
    if (latitude === 0 || longitude === 0) {
      console.log('Location not available for auto-fill');
      return; // Silently return - this is called automatically
    }

    setAutoFillLoading(true);
    try {
      const disableAddressCache = process.env.REACT_APP_DISABLE_ADDRESS_CACHE === 'true';
      const disableSimilarityMatch = process.env.REACT_APP_DISABLE_SIMILARITY_MATCH === 'true';
      // Get current user's warehouse information directly from user record
      const usersData = await getDocuments('users');
      const warehousesData = await getDocuments('warehouses');
      const citiesData = await getDocuments('cities');
      
      const currentUserData = usersData.find((user: any) => user.id === currentUser?.id);
      const userWarehouseId = currentUserData?.warehouseId || currentWarehouseId;
      
      // Allow warehouse/admin users (check both prop and actual user role)
      const isWarehouseOrAdmin = userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin';
      
      if (!userWarehouseId && !isWarehouseOrAdmin) {
        alert('No warehouse assignment found. Cannot get location.');
        return;
      }
      
      if (userWarehouseId && !currentWarehouseId) {
        setCurrentWarehouseId(userWarehouseId);
      }

      // Get the allowed city from warehouse (only for salesmen)
      const warehouse = warehousesData.find((w: any) => w.warehouseId === userWarehouseId);
      const allowedCity = (userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin') ? 
        undefined : // No city restriction for warehouse/admin
        warehouse?.cityCode ? 
          citiesData.find((c: any) => c.cityCode === warehouse.cityCode)?.name : undefined;

      // Parse address from geocoding with city validation
      const parseResult = await geocodingService.parseForOutletFields(
        latitude, 
        longitude, 
        allowedCity
      );
      
      // TEMPORARILY SUPPRESSED - CITY BOUNDARY VALIDATION
      // Handle city validation failure
      // if (!parseResult.success && parseResult.cityValidation && !parseResult.cityValidation.isAllowed) {
      //   alert(`❌ Location Validation Failed\n\n${parseResult.cityValidation.message}\n\nPlease choose a location within your assigned operating area.`);
      //   return;
      // }
      
      if (!parseResult.success || !parseResult.addressFields) {
        const details = (parseResult as any).error ? `\n\nDetails: ${(parseResult as any).error}` : '';
        alert(`Failed to extract address from location. Please try again.${details}`);
        return;
      }

      const addressFields = parseResult.addressFields;
      
      // Show city validation success message if available
      if (parseResult.cityValidation?.isAllowed) {
        console.log(`✅ City validation passed: ${parseResult.cityValidation.message}`);
      }

      // Check for similar areas and localities, then show unified address confirmation modal
      let existingArea = null;
      let existingLocalities: any[] = [];
      
      if (!disableSimilarityMatch && addressFields.suggestedAreaName) {
        let similarAreas = [];
        
        // For warehouse/admin users, search across ALL areas if no warehouseId
        const isWarehouseOrAdmin = userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin';
        
        console.log('🔍 Area search debug (getCurrentLocation):', {
          suggestedAreaName: addressFields.suggestedAreaName,
          userWarehouseId,
          isWarehouseOrAdmin,
          willSearchAllAreas: isWarehouseOrAdmin && !userWarehouseId,
          willSearchWarehouseAreas: !!userWarehouseId
        });
        
        if (isWarehouseOrAdmin && !userWarehouseId) {
          console.log('🔍 Warehouse user: searching ALL areas for:', addressFields.suggestedAreaName);
          // Get all areas and search manually since we don't have warehouse restriction
          const allAreas = await getDocuments('areas');
          const activeAreas = allAreas.filter((area: any) => area.isActive);
          
          console.log('📊 Total active areas found:', activeAreas.length);
          console.log('🗂️ All area names in database:', activeAreas.map((a: any) => a.name));
          
          // Manual similarity search across all areas
          similarAreas = activeAreas.filter((area: any) => {
            const areaNameLower = area.name.toLowerCase().trim();
            const suggestedAreaLower = addressFields.suggestedAreaName.toLowerCase().trim();
            
            // Multiple matching strategies
            const exactMatch = areaNameLower === suggestedAreaLower;
            const containsMatch = areaNameLower.includes(suggestedAreaLower) || suggestedAreaLower.includes(areaNameLower);
            
            // Clean matching - remove parentheses and normalize spaces
            const cleanAreaName = areaNameLower
              .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents
              .replace(/\s+/g, ' ')           // Normalize multiple spaces
              .trim();
            const cleanSuggestedName = suggestedAreaLower
              .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents  
              .replace(/\s+/g, ' ')           // Normalize multiple spaces
              .trim();
            const cleanMatch = cleanAreaName === cleanSuggestedName;
            
            // Extra fuzzy matching for common patterns like "West" vs "(West)"
            const fuzzyAreaName = areaNameLower
              .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
              .replace(/\s+/g, ' ')                   // Normalize spaces
              .trim();
            const fuzzySuggestedName = suggestedAreaLower
              .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
              .replace(/\s+/g, ' ')                   // Normalize spaces
              .trim();
            const fuzzyMatch = fuzzyAreaName === fuzzySuggestedName;
            
            const isMatch = exactMatch || containsMatch || cleanMatch || fuzzyMatch;
            
            if (isMatch) {
              console.log('✅ Area match found:', {
                dbAreaName: area.name,
                suggestedName: addressFields.suggestedAreaName,
                cleanDbName: cleanAreaName,
                cleanSuggested: cleanSuggestedName,
                fuzzyDbName: fuzzyAreaName,
                fuzzySuggested: fuzzySuggestedName,
                matchType: exactMatch ? 'exact' : containsMatch ? 'contains' : cleanMatch ? 'clean' : 'fuzzy'
              });
            }
            
            return isMatch;
          });
          
          console.log('🎯 Found similar areas across all warehouses:', similarAreas.map((a: any) => a.name));
        } else if (userWarehouseId) {
          // Salesman or warehouse user with warehouseId: use warehouse-restricted search
          console.log('👤 Searching areas within warehouse:', userWarehouseId);
          similarAreas = await findSimilarAreas(addressFields.suggestedAreaName, userWarehouseId);
        }
        
        if (similarAreas.length > 0) {
          existingArea = similarAreas[0];
          console.log('✅ Using existing area:', existingArea.name);
          // If area exists, check for similar localities
          if (addressFields.suggestedLocalityName) {
            existingLocalities = await findSimilarLocalities(addressFields.suggestedLocalityName, existingArea.id);
            console.log('🏘️ Found similar localities:', existingLocalities.map((l: any) => l.name));
          }
        } else {
          console.log('➕ No similar areas found - will create new area');
        }
      } else if (disableSimilarityMatch) {
        console.log('🔕 Similarity matching disabled via REACT_APP_DISABLE_SIMILARITY_MATCH');
      }

      // Show unified address confirmation modal
      setAddressConfirmationData({
        addressFields,
        warehouseId: userWarehouseId,
        existingArea,
        existingLocalities,
        editableAreaName: addressFields.suggestedAreaName || '',
        editableLocalityName: addressFields.suggestedLocalityName || ''
      });
      setShowAddressConfirmationModal(true);

    } catch (error) {
      console.error('Error auto-filling address:', error);
      alert('Error processing address. Please try again.');
    } finally {
      setAutoFillLoading(false);
    }
  };

  // Handle address confirmation from unified modal
  const handleAddressConfirmation = async (useExistingArea: boolean, useExistingLocality: boolean) => {
    if (!addressConfirmationData) return;

    try {
      const { addressFields, warehouseId, existingArea, existingLocalities, editableAreaName, editableLocalityName } = addressConfirmationData;
      
      let finalAreaId: string;
      let finalLocalityId: string;

      // Handle area creation/selection
      if (useExistingArea && existingArea) {
        finalAreaId = existingArea.id;
      } else {
        // Create new area with editable name
        finalAreaId = await createAreaFromGeocode(
          editableAreaName || 'Unknown Area',
          warehouseId,
          currentUser?.id || ''
        );
      }

      // Handle locality creation/selection
      if (useExistingLocality && existingLocalities.length > 0) {
        finalLocalityId = existingLocalities[0].id;
        // Update pincode if missing
        if (addressFields.pincode) {
          await updateLocalityPincode(finalLocalityId, addressFields.pincode);
        }
      } else {
        // Create new locality with editable name
        finalLocalityId = await createLocalityFromGeocode(
          editableLocalityName || 'Unknown Locality',
          finalAreaId,
          addressFields.pincode,
          currentUser?.id || ''
        );

        // Auto-assign route to the newly created locality
        await autoAssignRouteToNewLocality(finalLocalityId, {
          name: editableLocalityName || 'Unknown Locality',
          areaId: finalAreaId
        });
      }

      // Populate form fields
      await populateFormFields(addressFields, finalAreaId, finalLocalityId);
      
      setShowAddressConfirmationModal(false);
      setAddressConfirmationData(null);

    } catch (error) {
      console.error('Error handling address confirmation:', error);
      alert('Error processing address confirmation. Please try again.');
    }
  };

  const populateFormFields = async (addressFields: any, areaId: string, localityId: string) => {
    try {
      // Refresh areas and localities to include newly created ones
      await loadInitialData();
      
      // Get area and locality names
      const area = areas.find(a => a.id === areaId) || await (async () => {
        const areasData = await getDocuments('areas');
        return areasData.find((a: any) => a.id === areaId);
      })();
      
      const locality = localities.find(l => l.id === localityId) || await (async () => {
        const localitiesData = await getDocuments('localities');
        return localitiesData.find((l: any) => l.id === localityId);
      })();

      // Update form with auto-filled data
      setFormData(prev => ({
        ...prev,
        building: addressFields.building || prev.building,
        streetName: addressFields.streetName || prev.streetName,
        landmark: addressFields.landmark || prev.landmark,
        subLocality: addressFields.subLocality || prev.subLocality,
        areaId: areaId,
        areaName: (area as any)?.name || addressFields.suggestedAreaName,
        localityId: localityId,
        localityName: (locality as any)?.name || addressFields.suggestedLocalityName
      }));

      // Load localities for the selected area
      await loadLocalitiesForArea(areaId);

      // Location flow is now complete
      setLocationFlowActive(false);

    } catch (error) {
      console.error('Error populating form fields:', error);
      setLocationFlowActive(false); // Ensure freeze is removed on error
      alert('Error updating form fields. Please try again.');
    }
  };


  // Unified camera functions
  const openCamera = async (type: 'outlet' | 'visiting-card' | 'wtf-sticker') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setCameraStream(stream);
      setCameraType(type);
      setShowCamera(true);
    } catch (error) {
      alert('Unable to access camera. Please check permissions.');
    }
  };
  
  const handleCameraCapture = () => {
    // Find video element based on camera type
    let videoSelector = 'video';
    if (cameraType === 'outlet') {
      videoSelector = 'video[data-attribute="camera-video"]';
    } else if (cameraType === 'visiting-card') {
      videoSelector = 'video[data-attribute="visiting-card"]';
    } else if (cameraType === 'wtf-sticker') {
      videoSelector = 'video[data-attribute="wtf-sticker"]';
    }
    
    const video = document.querySelector(videoSelector) as HTMLVideoElement;
    if (!video || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      const imageDataURL = canvas.toDataURL('image/jpeg', 0.8);
      
      if (cameraType === 'outlet') {
        setFormData(prev => ({
          ...prev,
          outletInteriorExteriorPhotos: [...prev.outletInteriorExteriorPhotos, imageDataURL]
        }));
      } else if (cameraType === 'visiting-card') {
        setFormData(prev => ({
          ...prev,
          businessCardPhoto: imageDataURL
        }));
      } else if (cameraType === 'wtf-sticker') {
        setFormData(prev => ({
          ...prev,
          wtfBrandingStickerPhoto: imageDataURL
        }));
      }
      
      closeCameraModal();
    }
  };
  
  const closeCameraModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // QR Scanner Functions
  const startQRScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setQRScannerStream(stream);
      setShowQRScanner(true);
    } catch (error) {
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const processQR = async () => {
    if (!qrScannerStream) return;

    setIsProcessingQR(true);
    try {
      // Create a video element for QR scanning
      const video = document.createElement('video');
      video.srcObject = qrScannerStream;
      video.play();

      video.onloadedmetadata = async () => {
        try {
          const result = await QrScanner.scanImage(video);
          
          // Save the entire QR code content to dedicated field
          setFormData(prev => ({
            ...prev,
            qrCodeData: result
          }));
          stopQRScanner();
          alert('QR code content saved successfully!');
        } catch (error) {
          console.error('QR scanning failed:', error);
          alert('Could not read QR code. Please try again.');
        } finally {
          setIsProcessingQR(false);
        }
      };
    } catch (error) {
      console.error('QR scanner initialization failed:', error);
      alert('Failed to initialize QR scanner.');
      setIsProcessingQR(false);
    }
  };

  const stopQRScanner = () => {
    if (qrScannerStream) {
      qrScannerStream.getTracks().forEach((track: any) => track.stop());
      setQRScannerStream(null);
    }
    setShowQRScanner(false);
    setIsProcessingQR(false);
  };

  const handleSave = async () => {
    // Minimal validation - only location required
    if (formData.latitude === 0 || formData.longitude === 0) {
      alert('Please capture location first');
      return;
    }

    setSaving(true);
    try {
      if (editMode && existingOutlet) {
        // Edit mode - update existing outlet
        const addressComponents = [
          formData.building,
          formData.streetName,
          formData.localityName,
          formData.areaName
        ].filter(component => component.trim());
        
        const fullAddress = addressComponents.join(', ');

        const updateData = {
          name: formData.name.trim() || 'Unnamed Outlet',
          building: formData.building.trim() || '',
          streetName: formData.streetName.trim() || '',
          landmark: formData.landmark.trim() || '',
          subLocality: formData.subLocality.trim() || '',
          localityId: formData.localityId || '',
          address: fullAddress || 'Location captured',
          mobile: formData.mobile.trim() || '',
          classificationId: formData.classificationId || null,
          areaInSqft: formData.areaInSqft ? parseFloat(formData.areaInSqft) : null,
          rating: formData.rating || 0,
          latitude: formData.latitude,
          longitude: formData.longitude,
          mainOutletDisplayPhoto: formData.outletInteriorExteriorPhotos.length > 0 ? formData.outletInteriorExteriorPhotos[0] : null,
          outletInteriorExteriorPhotos: formData.outletInteriorExteriorPhotos,
          businessCardPhoto: formData.businessCardPhoto || null,
          wtfBrandingStickerPhoto: formData.wtfBrandingStickerPhoto || null,
          mobile2: formData.mobile2 || null,
          mobile3: formData.mobile3 || null,
          qrCodeData: formData.qrCodeData || null,
          remarks: formData.remarks || null,
          hasExoticFruits: formData.hasExoticFruits || false,
          hasHomeDelivery: formData.hasHomeDelivery || false,
          pincode: extractedAddressData?.addressFields?.pincode || extractedPincode || existingOutlet?.pincode || null,
          updatedBy: currentUser?.id || '',
          updatedDate: new Date()
        };

        if (externalOnSave) {
          // Use external save handler
          await externalOnSave(updateData);
          alert('Outlet updated successfully!');
        } else {
          // Import updateDocument function
          const { updateDocument } = await import('../services/firebase');
          await updateDocument('outlets', existingOutlet.documentId || existingOutlet.id, updateData);
          alert('Outlet updated successfully!');
        }
        onBack();
      } else {
        // Add mode - create new outlet with serial number
        const beatsData = await getDocuments('beats');
        const today = new Date().toISOString().split('T')[0].replace(/-/g, '-');
        const userActiveBeat = beatsData.find((beat: any) => 
          beat.salesmanId === currentUser?.id && 
          beat.isActive && 
          beat.forDate === today
        );

        if (!userActiveBeat || !userActiveBeat.routeId) {
          alert('No active route assignment found for today. Please contact administrator.');
          setSaving(false);
          return;
        }

        const userRouteId = userActiveBeat.routeId;

        // Final fail-safe city validation before saving
        const routesData = await getDocuments('routes');
        const warehousesData = await getDocuments('warehouses'); 
        const citiesData = await getDocuments('cities');

        const userRoute = routesData.find((route: any) => route.routeId === userRouteId);
        if (!userRoute || !userRoute.warehouseId || !userRoute.routeId) {
          alert('Invalid route configuration. Cannot save outlet.');
          setSaving(false);
          return;
        }

        const addressComponents = [
          formData.building,
          formData.streetName,
          formData.localityName,
          formData.areaName
        ].filter(component => component.trim());
        
        const fullAddress = addressComponents.join(', ');

        const outletData = {
          name: formData.name.trim() || 'Unnamed Outlet',
          building: formData.building.trim() || '',
          streetName: formData.streetName.trim() || '',
          landmark: formData.landmark.trim() || '',
          subLocality: formData.subLocality.trim() || '',
          localityId: formData.localityId || '',
          address: fullAddress || 'Location captured',
          mobile: formData.mobile.trim() || '',
          classificationId: formData.classificationId || null,
          areaInSqft: formData.areaInSqft ? parseFloat(formData.areaInSqft) : null,
          rating: formData.rating || 0,
          latitude: formData.latitude,
          longitude: formData.longitude,
          mainOutletDisplayPhoto: formData.outletInteriorExteriorPhotos.length > 0 ? formData.outletInteriorExteriorPhotos[0] : null,
          outletInteriorExteriorPhotos: formData.outletInteriorExteriorPhotos,
          businessCardPhoto: formData.businessCardPhoto || null,
          wtfBrandingStickerPhoto: formData.wtfBrandingStickerPhoto || null,
          mobile2: formData.mobile2 || null,
          mobile3: formData.mobile3 || null,
          qrCodeData: formData.qrCodeData || null,
          remarks: formData.remarks || null,
          hasExoticFruits: formData.hasExoticFruits || false,
          hasHomeDelivery: formData.hasHomeDelivery || false,
          pincode: extractedAddressData?.addressFields?.pincode || extractedPincode || null,
          addedBy: currentUser?.id || '',
          addedDate: new Date(),
          status: formData.rating >= 4 ? 'active' : 'pending_approval'
        };

        if (externalOnSave) {
          // Use external save handler
          await externalOnSave(outletData);
          const approvalMessage = formData.rating >= 4 
            ? `✅ Auto-approved (${formData.rating}-star rating)` 
            : `⏳ Pending backoffice approval (${formData.rating || 0}-star rating)`;
          alert(`Outlet saved successfully! ${approvalMessage}`);
        } else {
          // Use default internal save logic
          const result = await createOutletWithSerial(outletData);
          const approvalMessage = formData.rating >= 4 
            ? `\n✅ Auto-approved (${formData.rating}-star rating)` 
            : `\n⏳ Pending backoffice approval (${formData.rating || 0}-star rating)`;
          alert(`Outlet saved successfully! Outlet ID: ${result.outletId}${approvalMessage}`);
        }
        onBack();
      }
    } catch (error) {
      console.error('Error saving outlet:', error);
      alert('Error saving outlet. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mobile-container bg-gray-50 min-h-screen">
      {/* Freeze overlay during location flow */}
      {locationFlowActive && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Processing Location</h3>
            <p className="text-sm text-gray-600">Please wait while we confirm your location and process address details...</p>
          </div>
        </div>
      )}
      <HeaderSection 
        onBack={onBack}
        editMode={editMode}
        onScanCard={() => openCamera('visiting-card')}
      />

      {/* Form */}
      <div className="p-4 space-y-4 pb-24">
        <NameSection 
          name={formData.name}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={getPlaceholder('name', 'Enter outlet name')}
        />

        <AddressSection
          formData={formData}
          areas={areas}
          localities={localities}
          editMode={editMode}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          getPlaceholder={getPlaceholder}
          onAutoFill={() => handleAutoFillFromLocation()}
          autoFillLoading={autoFillLoading}
        />

        {/* Location Map & Edit Button - Show when coordinates are available */}
        {formData.latitude !== 0 && formData.longitude !== 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
            {/* Location Map */}
            <div className="mb-4">
              <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${formData.latitude},${formData.longitude}&zoom=16&maptype=roadmap`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Current Outlet Location"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                📍 Current outlet location on map
              </p>
            </div>
            
            <button
              onClick={handleEditLocation}
              className="w-full flex items-center justify-center space-x-2 bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>Edit Location</span>
            </button>
          </div>
        )}

        <MobileQRSection
          formData={formData}
          showAdditionalMobiles={showAdditionalMobiles}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          getPlaceholder={getPlaceholder}
          onToggleAdditionalMobiles={() => setShowAdditionalMobiles(!showAdditionalMobiles)}
          onStartQRScanner={startQRScanner}
          onClearQRData={() => setFormData(prev => ({ ...prev, qrCodeData: '' }))}
        />

        <RatingDetailsSection
          formData={formData}
          classifications={classifications}
          dataLoading={dataLoading}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          getPlaceholder={getPlaceholder}
          onRatingChange={(rating) => setFormData(prev => ({ ...prev, rating }))}
          onToggleExoticFruits={(value) => setFormData(prev => ({ ...prev, hasExoticFruits: value }))}
          onToggleHomeDelivery={(value) => setFormData(prev => ({ ...prev, hasHomeDelivery: value }))}
        />

        <PhotosDocumentsSection
          formData={{
            photos: formData.outletInteriorExteriorPhotos,
            visitingCardImage: formData.businessCardPhoto,
            wtfStickerImage: formData.wtfBrandingStickerPhoto
          }}
          editMode={editMode}
          existingOutlet={existingOutlet}
          onStartCamera={() => openCamera('outlet')}
          onStartWTFStickerScanner={() => openCamera('wtf-sticker')}
          onStartVisitingCardScanner={() => openCamera('visiting-card')}
          onRemovePhoto={(index) => setFormData(prev => ({ ...prev, outletInteriorExteriorPhotos: prev.outletInteriorExteriorPhotos.filter((_, i) => i !== index) }))}
          onClearVisitingCard={() => setFormData(prev => ({ ...prev, businessCardPhoto: '' }))}
          onClearWTFSticker={() => setFormData(prev => ({ ...prev, wtfBrandingStickerPhoto: '' }))}
        />

        {/* Status Management Section - For Warehouse/Admin Users Only */}
        {editMode && existingOutlet && (userRole === 'warehouse' || userRole === 'admin' || currentUser?.role === 'warehouse' || currentUser?.role === 'admin') && (
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-sm font-semibold">⚡</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Status Management</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Status
                </label>
                <div className="flex items-center space-x-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    existingOutlet.status === 'active' ? 'bg-green-100 text-green-800' :
                    existingOutlet.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                    existingOutlet.status === 'inactive' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {existingOutlet.status === 'pending_approval' ? 'Pending Approval' :
                     existingOutlet.status === 'active' ? 'Active' :
                     existingOutlet.status === 'inactive' ? 'Inactive' :
                     existingOutlet.status || 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Change Status To
                </label>
                <div className="flex flex-wrap gap-2">
                  {existingOutlet.status !== 'active' && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to approve this outlet and set it to Active?')) {
                          try {
                            const { updateDocument } = await import('../services/firebase');
                            await updateDocument('outlets', existingOutlet.documentId || existingOutlet.id, {
                              status: 'active',
                              approvedBy: currentUser?.id,
                              approvedAt: new Date(),
                              updatedAt: new Date(),
                              updatedBy: currentUser?.id
                            });
                            alert('Outlet status updated to Active successfully!');
                            onBack(); // Close the modal and refresh
                          } catch (error) {
                            console.error('Error updating status:', error);
                            alert('Failed to update outlet status. Please try again.');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      ✅ Approve & Activate
                    </button>
                  )}
                  
                  {existingOutlet.status !== 'inactive' && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to set this outlet to Inactive?')) {
                          try {
                            const { updateDocument } = await import('../services/firebase');
                            await updateDocument('outlets', existingOutlet.documentId || existingOutlet.id, {
                              status: 'inactive',
                              deactivatedBy: currentUser?.id,
                              deactivatedAt: new Date(),
                              updatedAt: new Date(),
                              updatedBy: currentUser?.id
                            });
                            alert('Outlet status updated to Inactive successfully!');
                            onBack(); // Close the modal and refresh
                          } catch (error) {
                            console.error('Error updating status:', error);
                            alert('Failed to update outlet status. Please try again.');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      ❌ Deactivate
                    </button>
                  )}
                  
                  {existingOutlet.status !== 'pending_approval' && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to set this outlet back to Pending Approval?')) {
                          try {
                            const { updateDocument } = await import('../services/firebase');
                            await updateDocument('outlets', existingOutlet.documentId || existingOutlet.id, {
                              status: 'pending_approval',
                              pendingBy: currentUser?.id,
                              pendingAt: new Date(),
                              updatedAt: new Date(),
                              updatedBy: currentUser?.id
                            });
                            alert('Outlet status updated to Pending Approval successfully!');
                            onBack(); // Close the modal and refresh
                          } catch (error) {
                            console.error('Error updating status:', error);
                            alert('Failed to update outlet status. Please try again.');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                    >
                      ⏳ Set Pending
                    </button>
                  )}
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mt-2">
                Status changes are logged with timestamp and user information.
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving || externalSaving || formData.latitude === 0}
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            !saving && !externalSaving && formData.latitude !== 0
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {(saving || externalSaving) ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{editMode ? 'Updating Outlet...' : 'Saving Outlet...'}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <Save className="w-5 h-5" />
              <span>{editMode ? 'Update Outlet' : 'Save Outlet'}</span>
            </div>
          )}
        </button>
      </div>

      {/* Unified Camera Modal */}
      <CameraModal
        isOpen={showCamera}
        cameraType={cameraType}
        stream={cameraStream}
        onClose={closeCameraModal}
        onCapture={handleCameraCapture}
      />

      {/* Map Modal */}
      <MapAdjustmentModal
        isOpen={showMapModal}
        initialLocation={{ lat: pendingLocation.lat, lng: pendingLocation.lng }}
        currentGPSLocation={currentLocation}
        onClose={() => setShowMapModal(false)}
        onConfirm={(location) => {
          // Put adjusted location into pending state
          setPendingLocation(location);
          setCurrentLocation(location);
          setShowMapModal(false);
          
          // Extract address and show confirmation modal (same flow as initial capture)
          extractAddressIfNeeded(location.lat, location.lng, true);
          setShowLocationConfirmationModal(true);
        }}
      />

      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showLocationModal}
        error={locationError}
        onClose={() => setShowLocationModal(false)}
        onRequestPermission={getCurrentLocation}
      />


      {/* Location Confirmation Modal */}
      {showLocationConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-8 p-3">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                <span>Confirm Location</span>
              </h2>
            </div>
            
            <div className="relative">
              <div ref={confirmationMapRef} className="w-full h-64" style={{ minHeight: '256px' }} />
              
              {!confirmationMapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              {/* Show area/locality validation if available */}
              {extractedAddressData ? (
                <div className="space-y-3 mb-4">
                  {/* Area Validation */}
                  {extractedAddressData.editableAreaName && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">🌆 {extractedAddressData.editableAreaName}</p>
                        <p className="text-xs text-gray-600">Auto-detected area</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {extractedAddressData.existingArea ? (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium text-green-700">Found</span>
                          </>
                        ) : (
                          <>
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-medium text-red-700">Will Create</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Locality Validation */}
                  {extractedAddressData.editableLocalityName && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">🏘️ {extractedAddressData.editableLocalityName}</p>
                        <p className="text-xs text-gray-600">Auto-detected locality</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {extractedAddressData.existingLocalities.length > 0 ? (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium text-green-700">Found</span>
                          </>
                        ) : (
                          <>
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-medium text-red-700">Will Create</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800 font-medium">📍 Location captured successfully</p>
                  <p className="text-xs text-blue-600">Ready to confirm this location</p>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={adjustLocationOnMap}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                >
                  Adjust on Map
                </button>
                <button
                  onClick={confirmPendingLocation}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All camera functionality now handled by unified CameraModal above */}

      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showQRScanner}
        stream={qrScannerStream}
        isProcessing={isProcessingQR}
        onClose={stopQRScanner}
        onScan={processQR}
      />

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
        <div className="flex justify-around py-2">
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="text-xs mt-1">Home</span>
          </button>
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <span className="text-xs mt-1">My Stock</span>
          </button>
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 13l-6-3" />
              </svg>
            </div>
            <span className="text-xs mt-1">Routes</span>
          </button>
          <button 
            className="flex flex-col items-center py-2 px-4 text-blue-600"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="text-xs mt-1">Outlets</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default AddOutlet;
